// src/components/VaultGallery.js
// Exclusive content vault — Gold sees basic, Premium sees more, Platinum sees all
// Sky Bri adds content from the admin panel
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import './VaultGallery.css'

// Faint repeating "who is watching" mark laid over vault media. It won't stop
// a determined person, but it makes leaking traceable and discourages it.
function Watermark({ text, count, size }) {
  return (
    <div className={`vault-watermark vault-watermark--${size}`} aria-hidden="true">
      <div className="vault-watermark__grid">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className="vault-watermark__text">{text}</span>
        ))}
      </div>
    </div>
  )
}

export default function VaultGallery({ tier }) {
  const rank = tier === 'platinum' ? 3 : tier === 'premium' ? 2 : 1
  const [content, setContent] = useState([])
  const [signedUrls, setSignedUrls] = useState({}) // { [item.id]: url }
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [newIds, setNewIds] = useState([]) // items posted since this member's last visit
  const { session, profile } = useAuth()
  const userId = session?.user?.id
  const watermarkText = `${profile?.display_name || 'Member'} · ${userId ? userId.slice(0, 6) : ''}`

  useEffect(() => {
    fetchVault()
  }, [])

  async function fetchVault() {
    const { data } = await supabase
      .from('vault_content')
      .select('*')
      .order('created_at', { ascending: false })

    if (data) {
      setContent(data)

      // "NEW" badges: compare against the newest item this member had seen
      // last time. (Uses the items' own timestamps, so device clock drift
      // can't cause false badges.) First-ever visit flags nothing.
      const seenKey = userId ? `skybri-vault-seen-${userId}` : null
      const newest = data.reduce((m, i) => (i.created_at > m ? i.created_at : m), '')
      if (seenKey && newest) {
        let lastSeen = null
        try { lastSeen = localStorage.getItem(seenKey) } catch (e) {}
        if (lastSeen) setNewIds(data.filter(i => i.created_at > lastSeen).map(i => i.id))
        // Mark as seen once they've had a moment to look at the vault
        setTimeout(() => { try { localStorage.setItem(seenKey, newest) } catch (e) {} }, 5000)
      }

      // Only ever request a signed URL for items this member's tier
      // actually unlocks — the storage policy would reject the rest
      // anyway, so there's no point asking (or leaking that we tried).
      const unlockedItems = data.filter(item => rank >= tierRank(item.min_tier))
      const entries = await Promise.all(
        unlockedItems.map(async item => {
          const { data: signed } = await supabase.storage
            .from('vault')
            .createSignedUrl(item.media_url, 3600) // 1 hour
          return [item.id, signed?.signedUrl || null]
        })
      )
      setSignedUrls(Object.fromEntries(entries))
    }
    setLoading(false)
  }

  function tierRank(t) {
    return t === 'platinum' ? 3 : t === 'premium' ? 2 : 1
  }

  function isUnlocked(item) {
    return rank >= tierRank(item.min_tier)
  }

  if (loading) return <div className="vault__loading">Loading vault...</div>

  if (content.length === 0) {
    return (
      <div className="vault__empty">
        <div className="vault__empty-icon">✦</div>
        <p>Exclusive content drops soon</p>
        <p className="vault__empty-sub">Check back often — this vault updates regularly</p>
      </div>
    )
  }

  return (
    <div className="vault">
      <div className="vault__header">
        <div>
          <h2 className="vault__title">Private Vault</h2>
          <p className="vault__sub">
            {rank === 1
              ? 'Upgrade to Premium or Platinum to unlock more exclusive content'
              : rank === 2
              ? 'Premium content unlocked. Upgrade to Platinum for the full vault.'
              : 'Full vault access — everything unlocked for Platinum members.'}
          </p>
          {newIds.length > 0 && (
            <span className="vault__new-pill">✦ {newIds.length} new since your last visit</span>
          )}
        </div>
        <div className="vault__count">
          <span className="vault__count-num">{content.filter(isUnlocked).length}</span>
          <span className="vault__count-label">unlocked</span>
        </div>
      </div>

      <div className="vault__grid">
        {content.map(item => {
          const unlocked = isUnlocked(item)
          return (
            <div
              key={item.id}
              className={`vault-item ${unlocked ? 'vault-item--unlocked' : 'vault-item--locked'}`}
              onClick={() => unlocked && setSelected(item)}
            >
              <div className="vault-item__media">
                {unlocked ? (
                  item.media_type === 'video' ? (
                    <div className="vault-item__video-thumb">
                      <div className="vault-item__play">▶</div>
                    </div>
                  ) : (
                    <img src={signedUrls[item.id]} alt={item.title} className="vault-item__img" />
                  )
                ) : (
                  <div className="vault-item__blur">
                    <div className="vault-item__lock-icon">◈</div>
                    <span className="vault-item__lock-tier">
                      {item.min_tier.charAt(0).toUpperCase() + item.min_tier.slice(1)}+
                    </span>
                  </div>
                )}
                {unlocked && item.media_type !== 'video' && (
                  <Watermark text={watermarkText} count={9} size="sm" />
                )}
                {newIds.includes(item.id) && (
                  <div className="vault-item__new-badge">New</div>
                )}
                {item.media_type === 'video' && (
                  <div className="vault-item__type-badge">Video</div>
                )}
              </div>
              <div className="vault-item__info">
                <p className="vault-item__title">{unlocked ? item.title : '••••••••'}</p>
                {item.description && unlocked && (
                  <p className="vault-item__desc">{item.description}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* LIGHTBOX */}
      {selected && (
        <div className="vault-lightbox" onClick={() => setSelected(null)}>
          <div className="vault-lightbox__inner" onClick={e => e.stopPropagation()}>
            <button className="vault-lightbox__close" onClick={() => setSelected(null)}>×</button>
            <div className="vault-lightbox__title">{selected.title}</div>
            <div className="vault-lightbox__frame">
              {selected.media_type === 'video' ? (
                <video
                  src={signedUrls[selected.id]}
                  controls
                  controlsList="nodownload"
                  onContextMenu={e => e.preventDefault()}
                  className="vault-lightbox__media"
                  autoPlay
                />
              ) : (
                <img
                  src={signedUrls[selected.id]}
                  alt={selected.title}
                  onContextMenu={e => e.preventDefault()}
                  draggable={false}
                  className="vault-lightbox__media"
                />
              )}
              <Watermark text={watermarkText} count={36} size="lg" />
            </div>
            {selected.description && (
              <p className="vault-lightbox__desc">{selected.description}</p>
            )}
          </div>
        </div>
      )}

      {/* UPGRADE TEASER for non-platinum */}
      {rank < 3 && content.some(c => !isUnlocked(c)) && (
        <div className="vault__teaser">
          <p className="vault__teaser-text">
            {content.filter(c => !isUnlocked(c)).length} more items locked.
            Upgrade your card to unlock the full vault.
          </p>
        </div>
      )}
    </div>
  )
}
